import { API_URL } from "../../config";

/* ═══════════════════════════════════════════════════════════════════════════
   UNIFIED LEAD SYSTEM — API SERVICE & TYPES
   Single Source of Truth for Inquiry / Pipeline / Calendar / Dashboard
   ═══════════════════════════════════════════════════════════════════════════ */

const hdrs = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { ...hdrs(), ...((opts.headers as Record<string, string>) || {}) },
  });
  const json = await res.json();
  if (!res.ok) throw { status: res.status, ...json };
  return json.data;
}

const qs = (f: Record<string, any>): string => {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
};

/* ── Types ──────────────────────────────────────────────────────────────── */

export type LeadStatus =
  | "new"
  | "contacted"
  | "negotiation"
  | "ready_for_quote"
  | "ready_for_booking"
  | "confirmed"
  | "lost";

export type LeadSource = "website" | "email" | "phone" | "walk_in" | "manual";
export type AiScore = "hot" | "warm" | "cold";
export type Priority = "low" | "medium" | "high" | "urgent";

export interface LeadThread {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  organization: string | null;
  event_type: string | null;
  event_date: string | null;
  guest_count: number | null;
  budget_known: boolean;
  budget_value: number | null;
  preferred_hall: string | null;
  status: LeadStatus;
  source: LeadSource;
  ai_score: AiScore | null;
  ai_summary: string | null;
  ai_extracted_data: Record<string, any> | null;
  assigned_to: number | null;
  assigned_at: string | null;
  priority: Priority;
  follow_up_required: boolean;
  follow_up_at: string | null;
  is_vip: boolean;
  first_response_at: string | null;
  response_delay_minutes: number | null;
  booking_id: number | null;
  quote_id: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Eager-loaded
  assignee?: TeamUser | null;
  inquiry?: LeadInquiry | null;
  inquiries?: LeadInquiry[];
  lead_notes?: LeadNote[];
  activities?: LeadActivity[];
  email_threads?: EmailThreadLink[];
}

export interface LeadInquiry {
  id: number;
  thread_id: number;
  message: string | null;
  raw_payload: Record<string, any> | null;
  created_at: string;
}

export interface LeadNote {
  id: number;
  thread_id: number;
  user_id: number | null;
  note: string;
  created_at: string;
}

export interface LeadActivity {
  id: number;
  thread_id: number;
  user_id: number | null;
  type: string;
  description: string | null;
  meta: Record<string, any> | null;
  created_at: string;
}

export interface EmailThreadLink {
  id: number;
  subject: string;
  customer_email: string | null;
  linked_thread_id: number | null;
  is_private: boolean;
  last_message_at: string | null;
  unread_count: number;
  emails?: any[];
}

export interface TeamUser {
  id: number;
  username: string;
}

export interface AiSuggestion {
  type: "hall" | "pricing" | "next_action";
  title: string;
  value: string;
  reason: string;
}

export interface CalendarEvent {
  id: number;
  title: string;
  start: string;
  end: string;
  status: LeadStatus;
  color: string;
  guest_count: number | null;
  event_type: string | null;
  lead: LeadThread;
}

export interface DashboardStats {
  total: number;
  by_status: Record<LeadStatus, number>;
  hot_leads: number;
  unassigned: number;
  follow_up_required: number;
  avg_response_minutes: number;
  this_week: number;
  last_week: number;
  conversion_rate: number;
}

export interface DashboardCharts {
  leads_by_day: Record<string, number>;
  funnel: { status: string; count: number }[];
  event_types: { event_type: string; count: number }[];
  sources: { source: string; count: number }[];
  ai_scores: { ai_score: string; count: number }[];
}

export interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

/* ── Constants ─────────────────────────────────────────────────────────── */

export const STATUS_CONFIG: Record<
  LeadStatus,
  { label: string; color: string; bgColor: string }
> = {
  new: { label: "New Inquiry", color: "#3b82f6", bgColor: "#eff6ff" },
  contacted: { label: "Contacted", color: "#8b5cf6", bgColor: "#f5f3ff" },
  negotiation: { label: "Negotiation", color: "#f59e0b", bgColor: "#fffbeb" },
  ready_for_quote: { label: "Ready for Quote", color: "#14b8a6", bgColor: "#f0fdfa" },
  ready_for_booking: { label: "Ready for Booking", color: "#22c55e", bgColor: "#f0fdf4" },
  confirmed: { label: "Confirmed", color: "#16a34a", bgColor: "#dcfce7" },
  lost: { label: "Lost", color: "#ef4444", bgColor: "#fef2f2" },
};

export const AI_SCORE_CONFIG: Record<
  AiScore,
  { label: string; color: string; icon: string }
> = {
  hot: { label: "Hot", color: "#ef4444", icon: "🔥" },
  warm: { label: "Warm", color: "#f59e0b", icon: "☀️" },
  cold: { label: "Cold", color: "#3b82f6", icon: "❄️" },
};

export const PRIORITY_CONFIG: Record<
  Priority,
  { label: string; color: string }
> = {
  low: { label: "Low", color: "#6b7280" },
  medium: { label: "Medium", color: "#3b82f6" },
  high: { label: "High", color: "#f59e0b" },
  urgent: { label: "Urgent", color: "#ef4444" },
};

export const PIPELINE_ORDER: LeadStatus[] = [
  "new",
  "contacted",
  "negotiation",
  "ready_for_quote",
  "ready_for_booking",
  "confirmed",
  "lost",
];

/* ── API Functions ─────────────────────────────────────────────────────── */

// List & Search
export const fetchLeads = (filters: Record<string, any> = {}) =>
  apiFetch<LeadThread[]>(`/leads${qs(filters)}`);

export const fetchLeadsWithMeta = async (
  filters: Record<string, any> = {}
): Promise<{ data: LeadThread[]; meta: PaginationMeta }> => {
  const res = await fetch(`${API_URL}/leads${qs(filters)}`, {
    headers: hdrs(),
  });
  const json = await res.json();
  if (!res.ok) throw json;
  return { data: json.data, meta: json.meta };
};

// Single Thread Detail
export const fetchLeadThread = (id: number) =>
  apiFetch<LeadThread>(`/leads/${id}`);

// Create (manual)
export const createLead = (data: Partial<LeadThread>) =>
  apiFetch<LeadThread>("/leads", {
    method: "POST",
    body: JSON.stringify(data),
  });

// Update
export const updateLead = (id: number, data: Partial<LeadThread>) =>
  apiFetch<LeadThread>(`/leads/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });

// Update status (drag-drop)
export const updateLeadStatus = (id: number, status: LeadStatus) =>
  apiFetch<LeadThread>(`/leads/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });

// Website inquiry
export const submitInquiry = (data: Record<string, any>) =>
  apiFetch<LeadThread>("/leads/inquiry", {
    method: "POST",
    body: JSON.stringify(data),
  });

// Convert email → lead
export const convertEmailToLead = (data: {
  email_thread_id: number;
  name: string;
  [key: string]: any;
}) =>
  apiFetch<LeadThread>("/leads/convert-email", {
    method: "POST",
    body: JSON.stringify(data),
  });

// Link email to lead
export const linkEmailToLead = (
  emailThreadId: number,
  leadThreadId: number
) =>
  apiFetch<void>("/leads/link-email", {
    method: "POST",
    body: JSON.stringify({
      email_thread_id: emailThreadId,
      lead_thread_id: leadThreadId,
    }),
  });

// Unlink email
export const unlinkEmailFromLead = (emailThreadId: number) =>
  apiFetch<void>(`/leads/unlink-email/${emailThreadId}`, {
    method: "DELETE",
  });

// Email privacy
export const setEmailPrivacy = (emailThreadId: number, isPrivate: boolean) =>
  apiFetch<void>(`/leads/email-privacy/${emailThreadId}`, {
    method: "PATCH",
    body: JSON.stringify({ is_private: isPrivate }),
  });

// Notes
export const addLeadNote = (threadId: number, note: string) =>
  apiFetch<LeadNote>(`/leads/${threadId}/notes`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });

export const deleteLeadNote = (noteId: number) =>
  apiFetch<void>(`/leads/notes/${noteId}`, { method: "DELETE" });

// Pipeline
export const fetchPipeline = () =>
  apiFetch<Record<LeadStatus, LeadThread[]>>("/leads/view/pipeline");

// Calendar
export const fetchCalendarEvents = (from: string, to: string) =>
  apiFetch<CalendarEvent[]>(`/leads/view/calendar?from=${from}&to=${to}`);

// Dashboard
export const fetchDashboardStats = () =>
  apiFetch<DashboardStats>("/leads/view/dashboard-stats");

export const fetchDashboardCharts = () =>
  apiFetch<DashboardCharts>("/leads/view/dashboard-charts");

// AI Suggestions
export const fetchAiSuggestions = (id: number) =>
  apiFetch<AiSuggestion[]>(`/leads/${id}/ai-suggestions`);

// Team
export const fetchTeamUsers = () =>
  apiFetch<TeamUser[]>("/leads/view/team-users");

// AI Classification
export interface ClassificationResult {
  status: string;
  confidence: "high" | "medium" | "low" | "none";
  score: number;
  scores: Record<string, number>;
  applied?: boolean;
  reason?: string;
}

export const classifyEmailThread = (emailThreadId: number) =>
  apiFetch<ClassificationResult>(`/leads/classify/${emailThreadId}`);

export const reclassifyAllLeads = () =>
  apiFetch<{ count: number; message: string }>("/leads/reclassify-all", {
    method: "POST",
  });
